import React from 'react';
import {
  Box, Typography, Paper, TextField, Button, Grid, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Chip, Card, CardContent,
  FormControl, InputLabel, Select, MenuItem, Checkbox,
  TablePagination
} from '@mui/material';
import {
  Add, Edit, Delete, Receipt, CheckCircle, Pending, Payment,
  EditOutlined, DeleteOutlined, PaymentOutlined, CheckCircleOutlined,
  HourglassEmpty, Search
} from '@mui/icons-material';
import DateRangePickerField from '../components/DateRangePickerField';

export default function InvoicesTab({
  filteredInvoices,
  invoiceStats,
  categories,
  suppliers,
  selectedInvoices,
  startDate,
  endDate,
  filterSupplier,
  filterCategory,
  filterStatus,
  invoiceSearchTerm,
  invoicePage,
  invoiceRowsPerPage,
  invoicesPagination,
  syncing,
  isMobile,
  formatCurrency,
  navigate,
  onStartDateChange,
  onEndDateChange,
  onSupplierChange,
  onCategoryChange,
  onStatusChange,
  onSearchChange,
  onPageChange,
  onRowsPerPageChange,
  onAdd,
  onEdit,
  onDelete,
  onPayment,
  onMarkPaid,
  onOpenPreview,
  onSelectInvoice,
  onSelectAllInvoices,
  onBulkDelete,
  onSyncMydata,
}) {
  return (
    <>
      {/* Stats Cards */}
      <Grid container spacing={{ xs: 1.5, md: 3 }} sx={{ mb: { xs: 2, md: 3 } }}>
        <Grid item xs={4} md={4}>
          <Card sx={{ 
            borderRadius: 3,
            background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
            boxShadow: '0 8px 32px rgba(139, 92, 246, 0.3)',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 40px rgba(139, 92, 246, 0.4)' }
          }}>
            <CardContent sx={{ p: { xs: 1.5, md: 2.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
                    Σύνολο Τιμολογίων
                  </Typography>
                  <Typography variant={isMobile ? 'h6' : 'h4'} sx={{ fontWeight: 700, color: '#fff', my: 0.5 }}>
                    {formatCurrency(invoiceStats.total_amount)}
                  </Typography>
                </Box>
                {!isMobile && <Receipt sx={{ fontSize: 48, color: 'rgba(255,255,255,0.25)' }} />}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4} md={4}>
          <Card sx={{ 
            borderRadius: 3,
            background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
            boxShadow: '0 8px 32px rgba(34, 197, 94, 0.3)',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 40px rgba(34, 197, 94, 0.4)' }
          }}>
            <CardContent sx={{ p: { xs: 1.5, md: 2.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
                    Πληρωμένα
                  </Typography>
                  <Typography variant={isMobile ? 'h6' : 'h4'} sx={{ fontWeight: 700, color: '#fff', my: 0.5 }}>
                    {formatCurrency(invoiceStats.paid_amount)}
                  </Typography>
                </Box>
                {!isMobile && <CheckCircle sx={{ fontSize: 48, color: 'rgba(255,255,255,0.25)' }} />}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4} md={4}>
          <Card sx={{ 
            borderRadius: 3,
            background: 'linear-gradient(135deg, #eab308 0%, #ca8a04 100%)',
            boxShadow: '0 8px 32px rgba(234, 179, 8, 0.3)',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 40px rgba(234, 179, 8, 0.4)' }
          }}>
            <CardContent sx={{ p: { xs: 1.5, md: 2.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
                    Εκκρεμή
                  </Typography>
                  <Typography variant={isMobile ? 'h6' : 'h4'} sx={{ fontWeight: 700, color: '#fff', my: 0.5 }}>
                    {formatCurrency(invoiceStats.pending_amount)}
                  </Typography>
                </Box>
                {!isMobile && <HourglassEmpty sx={{ fontSize: 48, color: 'rgba(255,255,255,0.25)' }} />}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ 
        p: 2, mb: 3, borderRadius: 3,
        border: '1px solid rgba(0,0,0,0.06)',
        background: 'rgba(139,92,246,0.02)'
      }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <DateRangePickerField
              startDate={startDate}
              endDate={endDate}
              onRangeChange={(start, end) => {
                onStartDateChange(start);
                onEndDateChange(end);
              }}
              isMobile={isMobile}
              label="Περίοδος"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Προμηθευτής</InputLabel>
              <Select value={filterSupplier} onChange={(e) => onSupplierChange(e.target.value)} sx={{ borderRadius: 2 }}>
                <MenuItem value="">Όλοι</MenuItem>
                {suppliers.map(sup => (
                  <MenuItem key={sup.id} value={sup.id}>{sup.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Κατηγορία</InputLabel>
              <Select value={filterCategory} onChange={(e) => onCategoryChange(e.target.value)} sx={{ borderRadius: 2 }}>
                <MenuItem value="">Όλες</MenuItem>
                {categories.map(cat => (
                  <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Κατάσταση</InputLabel>
              <Select value={filterStatus} onChange={(e) => onStatusChange(e.target.value)} sx={{ borderRadius: 2 }}>
                <MenuItem value="">Όλα</MenuItem>
                <MenuItem value="pending">Εκκρεμή</MenuItem>
                <MenuItem value="partial">Μερικώς Εξοφλημένα</MenuItem>
                <MenuItem value="paid">Πληρωμένα</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Toolbar */}
      <Paper sx={{ 
        p: { xs: 1.5, md: 2 }, mb: 2, borderRadius: 3,
        border: '1px solid rgba(0,0,0,0.06)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1,
        flexWrap: 'wrap'
      }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: 1 }}>
          <TextField
            size="small"
            placeholder="Αναζήτηση τιμολογίου..."
            value={invoiceSearchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            sx={{ minWidth: { xs: 150, md: 250 }, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />,
            }}
          />
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={onSyncMydata}
            disabled={syncing}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            {syncing ? 'Συγχρονισμός...' : 'myDATA Sync'}
          </Button>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          onClick={onAdd}
        >
          Νέο Τιμολόγιο
        </Button>
      </Paper>

      {selectedInvoices.length > 0 && (
        <Box sx={{ 
          mb: 2, p: 2, borderRadius: 3, 
          bgcolor: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between' 
        }}>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            Επιλέχθηκαν {selectedInvoices.length} τιμολόγια
          </Typography>
          <Button
            variant="contained"
            color="error"
            size="small"
            startIcon={<Delete />}
            onClick={onBulkDelete}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            Διαγραφή Επιλεγμένων
          </Button>
        </Box>
      )}

      {/* Mobile: Card Layout for Invoices */}
      {isMobile ? (
        <Stack spacing={1.5}>
          {filteredInvoices.map((invoice) => (
            <Card key={invoice.id} variant="outlined">
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                {/* Row 1: Date + Invoice Number + Status */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {invoice.invoice_date}
                    </Typography>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        fontWeight: 600,
                        cursor: 'pointer',
                        color: 'primary.main',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                      onClick={() => onOpenPreview(invoice)}
                    >
                      #{invoice.invoice_number}
                    </Typography>
                  </Box>
                  {invoice.payment_status === 'paid' ? (
                    <Chip icon={<CheckCircle />} label="Πληρωμένο" color="success" size="small" />
                  ) : invoice.payment_status === 'partial' ? (
                    <Chip icon={<Pending />} label="Μερικώς" color="info" size="small" />
                  ) : (
                    <Chip icon={<Pending />} label="Εκκρεμές" color="warning" size="small" />
                  )}
                </Box>
                
                {/* Row 2: Supplier + Category */}
                <Box sx={{ mb: 1 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 500,
                      cursor: 'pointer',
                      color: 'primary.main',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                    onClick={() => navigate(`/suppliers/${invoice.supplier_id}`)}
                  >
                    {invoice.supplier_name}
                  </Typography>
                  {invoice.category_name && (
                    <Typography variant="caption" color="text.secondary">
                      {invoice.category_name}
                    </Typography>
                  )}
                </Box>
                
                {/* Row 3: Amount + Payment Terms */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Ποσό</Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {formatCurrency(invoice.amount)}
                    </Typography>
                  </Box>
                  <Chip
                    label={invoice.payment_terms === 'cash' ? 'Μετρητά' : 'Πίστωση'}
                    color={invoice.payment_terms === 'cash' ? 'primary' : 'default'}
                    size="small"
                    variant="outlined"
                  />
                </Box>
                
                {/* Row 4: Actions */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  gap: 1,
                  pt: 1.5,
                  borderTop: '1px solid',
                  borderColor: 'divider'
                }}>
                  {(invoice.payment_status === 'pending' || invoice.payment_status === 'partial') && (
                    <>
                      <IconButton
                        onClick={() => onPayment(invoice)}
                        sx={{ 
                          width: 44,
                          height: 44,
                          borderRadius: '14px',
                          border: '2px solid #3b82f6',
                          color: '#3b82f6',
                          bgcolor: 'transparent',
                          transition: 'all 0.2s ease',
                          '&:hover': { 
                            bgcolor: '#3b82f6',
                            color: '#fff',
                            transform: 'scale(1.05)',
                          }
                        }}
                      >
                        <PaymentOutlined sx={{ fontSize: 22 }} />
                      </IconButton>
                      <IconButton
                        onClick={() => onMarkPaid(invoice.id)}
                        sx={{ 
                          width: 44,
                          height: 44,
                          borderRadius: '14px',
                          border: '2px solid #22c55e',
                          color: '#22c55e',
                          bgcolor: 'transparent',
                          transition: 'all 0.2s ease',
                          '&:hover': { 
                            bgcolor: '#22c55e',
                            color: '#fff',
                            transform: 'scale(1.05)',
                          }
                        }}
                      >
                        <CheckCircleOutlined sx={{ fontSize: 22 }} />
                      </IconButton>
                    </>
                  )}
                  <IconButton
                    onClick={() => onEdit(invoice)}
                    sx={{ 
                      width: 44,
                      height: 44,
                      borderRadius: '14px',
                      border: '2px solid #64748b',
                      color: '#64748b',
                      bgcolor: 'transparent',
                      transition: 'all 0.2s ease',
                      '&:hover': { 
                        bgcolor: '#64748b',
                        color: '#fff',
                        transform: 'scale(1.05)',
                      }
                    }}
                  >
                    <EditOutlined sx={{ fontSize: 22 }} />
                  </IconButton>
                  <IconButton
                    onClick={() => onDelete(invoice.id)}
                    sx={{ 
                      width: 44,
                      height: 44,
                      borderRadius: '14px',
                      border: '2px solid #ef4444',
                      color: '#ef4444',
                      bgcolor: 'transparent',
                      transition: 'all 0.2s ease',
                      '&:hover': { 
                        bgcolor: '#ef4444',
                        color: '#fff',
                        transform: 'scale(1.05)',
                      }
                    }}
                  >
                    <DeleteOutlined sx={{ fontSize: 22 }} />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        /* Desktop: Table Layout */
        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.06)' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={filteredInvoices.length > 0 && selectedInvoices.length === filteredInvoices.length}
                    indeterminate={selectedInvoices.length > 0 && selectedInvoices.length < filteredInvoices.length}
                    onChange={onSelectAllInvoices}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary' }}>Ημερομηνία</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary' }}>Αριθμός</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary' }}>Προμηθευτής</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary' }}>Κατηγορία</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary' }}>Ποσό</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary' }}>Όροι</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary' }}>Κατάσταση</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary' }} align="right">Ενέργειες</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredInvoices.map((invoice) => (
                <TableRow key={invoice.id} selected={selectedInvoices.includes(invoice.id)} sx={{ transition: 'background 0.15s', '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' } }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedInvoices.includes(invoice.id)}
                      onChange={() => onSelectInvoice(invoice.id)}
                    />
                  </TableCell>
                  <TableCell>{invoice.invoice_date}</TableCell>
                  <TableCell>
                    <Typography
                      component="span"
                      sx={{ 
                        cursor: 'pointer',
                        color: 'primary.main',
                        fontWeight: 500,
                        '&:hover': { textDecoration: 'underline' }
                      }}
                      onClick={() => onOpenPreview(invoice)}
                    >
                      {invoice.invoice_number}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography 
                      component="span"
                      sx={{ 
                        cursor: 'pointer',
                        color: 'primary.main',
                        fontWeight: 500,
                        '&:hover': { textDecoration: 'underline' }
                      }}
                      onClick={() => navigate(`/suppliers/${invoice.supplier_id}`)}
                    >
                      {invoice.supplier_name}
                    </Typography>
                    {invoice.supplier_name?.startsWith('Προμηθευτής') && (
                      <Typography variant="caption" color="textSecondary" display="block">
                        ΑΦΜ: {invoice.supplier_name.replace('Προμηθευτής ', '')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{invoice.category_name}</TableCell>
                  <TableCell>{formatCurrency(invoice.amount)}</TableCell>
                  <TableCell>
                    <Chip
                      label={invoice.payment_terms === 'cash' ? 'Μετρητά' : 'Πίστωση'}
                      color={invoice.payment_terms === 'cash' ? 'primary' : 'default'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {invoice.payment_status === 'paid' ? (
                      <Chip
                        icon={<CheckCircle />}
                        label={`Πληρωμένο (${invoice.payment_date})`}
                        color="success"
                        size="small"
                      />
                    ) : invoice.payment_status === 'partial' ? (
                      <Chip
                        icon={<Payment />}
                        label="Μερικώς Εξοφλημένο"
                        color="info"
                        size="small"
                      />
                    ) : (
                      <Chip
                        icon={<Pending />}
                        label="Εκκρεμές"
                        color="warning"
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {(invoice.payment_status === 'pending' || invoice.payment_status === 'partial') && (
                      <>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => onPayment(invoice)}
                          title="Καταχώρηση Πληρωμής"
                        >
                          <Payment />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => onMarkPaid(invoice.id)}
                          title="Απλή Σημείωση ως Πληρωμένο"
                        >
                          <CheckCircle />
                        </IconButton>
                      </>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => onEdit(invoice)}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => onDelete(invoice.id)}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <TablePagination
        component="div"
        count={invoicesPagination.total || 0}
        page={invoicePage}
        onPageChange={onPageChange}
        rowsPerPage={invoiceRowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[10, 50, 100]}
        labelRowsPerPage="Εμφάνιση ανά:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} από ${count !== -1 ? count : `περισσότερα από ${to}`}`}
      />
    </>
  );
}
